/**
 * ReportsPage — financial overview at /reports.
 *
 * Layout (top-to-bottom):
 *   1. Header: title + DateRangePicker (preset chips + custom dates).
 *   2. Filters strip: dealer / brand / model / risk-class / status[] —
 *      narrow inline above the KPIs so the operator sees what's filtered
 *      while reading numbers.
 *   3. KPI cards row (responsive grid): Financed, Collections, Profit,
 *      Outstanding, Overdue, Active.
 *   4. Two-column section: revenue trend (LineChart) + status donut.
 *   5. Top devices bar (HorizontalBars).
 *
 * Data: POST /api/reports/financial — server holds the heavy aggregates.
 * The page re-fires the query on EVERY filter change, debounced
 * indirectly via TanStack Query's natural batching.
 *
 * Permission: gated by `report.read` (route-level + sidebar).
 */
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  Dealer,
  Device,
  FinancialReportFilter,
  FinancialReportResult,
  LoanStatus,
} from "@dfp/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CollapsibleCard } from "../components/CollapsibleCard.js";
import { DateRangePicker, rangeForPreset, type DateRange } from "../components/DateRangePicker.js";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown.js";
import { LineChart } from "../components/charts/LineChart.js";
import { HorizontalBars } from "../components/charts/HorizontalBars.js";
import { StatusDonut } from "../components/charts/StatusDonut.js";
import { api } from "../lib/api.js";

const RISK_CLASSES = ["A", "B", "C"] as const;
const STATUS_CHOICES: readonly LoanStatus[] = [
  "DRAFT",
  "PENDING_VERIFICATION",
  "PENDING_DEPOSIT",
  "PENDING_DEVICE_PROVISIONING",
  "ACTIVE",
  "IN_ARREARS",
  "LOCKED",
  "IN_DISPUTE",
  "PENDING_RELEASE",
  "PAID_OFF",
  "WRITTEN_OFF",
  "REJECTED",
];

export function ReportsPage(): JSX.Element {
  const [range, setRange] = useState<DateRange>(() => rangeForPreset("30d"));
  const [dealerId, setDealerId] = useState<string>("");
  const [brands, setBrands] = useState<readonly string[]>([]);
  const [models, setModels] = useState<readonly string[]>([]);
  const [riskClass, setRiskClass] = useState<readonly ("A" | "B" | "C")[]>([]);
  const [statuses, setStatuses] = useState<readonly LoanStatus[]>([]);

  // Dealers + devices lists — devices feed the brand/model dropdowns.
  // Both are country-scoped + small at MVP volume.
  const dealersQuery = useQuery({
    queryKey: ["dealers", "list", "for-reports"],
    queryFn: () => api.get<readonly Dealer[]>("/api/dealers"),
  });
  const devicesQuery = useQuery({
    queryKey: ["devices", "list", "for-reports"],
    queryFn: () => api.get<readonly Device[]>("/api/devices"),
  });

  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of devicesQuery.data ?? []) set.add(d.brand);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devicesQuery.data]);
  const modelOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of devicesQuery.data ?? []) {
      if (brands.length === 0 || brands.includes(d.brand)) set.add(d.model);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devicesQuery.data, brands]);

  // Drop now-orphan model selections when brands change. Same logic
  // as LoansPage — keeps the filter coherent.
  useEffect(() => {
    if (brands.length === 0) return;
    const allowed = new Set<string>();
    for (const d of devicesQuery.data ?? []) {
      if (brands.includes(d.brand)) allowed.add(d.model);
    }
    const next = models.filter((m) => allowed.has(m));
    if (next.length !== models.length) setModels(next);
  }, [brands, devicesQuery.data, models]);

  const filter: FinancialReportFilter = useMemo(
    () => ({
      startAt: range.startAt,
      endAt: range.endAt,
      bucket: range.bucket,
      ...(dealerId ? { dealerId } : {}),
      ...(brands.length > 0 ? { brands } : {}),
      ...(models.length > 0 ? { models } : {}),
      ...(riskClass.length > 0 ? { modelRiskClass: riskClass } : {}),
      ...(statuses.length > 0 ? { status: statuses } : {}),
    }),
    [range, dealerId, brands, models, riskClass, statuses],
  );

  const anyFilterActive =
    dealerId.length > 0 ||
    brands.length > 0 ||
    models.length > 0 ||
    riskClass.length > 0 ||
    statuses.length > 0;

  function clearFilters(): void {
    setDealerId("");
    setBrands([]);
    setModels([]);
    setRiskClass([]);
    setStatuses([]);
  }

  const report = useQuery({
    queryKey: ["reports", "financial", filter],
    queryFn: () => api.post<FinancialReportResult>("/api/reports/financial", filter),
    // v5 rename: `keepPreviousData` is now the `placeholderData`
    // helper. Keeps the previous report on screen during a filter
    // change so the KPI tiles don't blink to "—".
    placeholderData: keepPreviousData,
  });

  const data = report.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-simuflex-navy">Financial reports</h1>
        <p className="text-sm text-simuflex-slate">
          Disbursements, collections, profit and arrears across the chosen window. Filters narrow to
          a dealer, device, risk class, or loan status.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Date range</CardTitle>
        </CardHeader>
        <CardBody>
          <DateRangePicker value={range} onChange={setRange} />
        </CardBody>
      </Card>

      {/*
        Filters — collapsed by default so the KPIs + charts stay
        prominent. The dot in the header signals active filters
        even while collapsed.
      */}
      <CollapsibleCard
        title="Filters"
        defaultOpen={false}
        hasActiveContent={anyFilterActive}
        headerAction={
          anyFilterActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate hover:text-simuflex-navy"
            >
              Clear all
            </button>
          ) : null
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Dealer
            </label>
            <select
              value={dealerId}
              onChange={(e) => setDealerId(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-simuflex-line bg-white px-2 py-1.5 text-simuflex-navy"
            >
              <option value="">All dealers</option>
              {(dealersQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          {/* Brand multi-select — sourced from devices list. */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Brand
            </label>
            <div className="mt-1">
              <MultiSelectDropdown
                label="Brand"
                options={brandOptions}
                selected={brands}
                onChange={setBrands}
                placeholder="All brands"
              />
            </div>
          </div>
          {/* Model multi-select — narrows by brand. */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Model
            </label>
            <div className="mt-1">
              <MultiSelectDropdown
                label="Model"
                options={modelOptions}
                selected={models}
                onChange={setModels}
                placeholder={brands.length > 0 ? "All models for these brands" : "All models"}
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Risk class
            </label>
            <div className="mt-1 flex gap-2">
              {RISK_CLASSES.map((c) => {
                const active = riskClass.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setRiskClass(active ? riskClass.filter((x) => x !== c) : [...riskClass, c])
                    }
                    className={`px-3 py-1 rounded-full text-xs font-bold border ${
                      active
                        ? "bg-simuflex-navy text-white border-simuflex-navy"
                        : "bg-white text-simuflex-navy border-simuflex-line"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-[10px] font-bold uppercase text-simuflex-slate tracking-wider">
              Status (any of)
            </label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {STATUS_CHOICES.map((s) => {
                const active = statuses.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() =>
                      setStatuses(active ? statuses.filter((x) => x !== s) : [...statuses, s])
                    }
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border tracking-wider ${
                      active
                        ? "bg-simuflex-navy text-white border-simuflex-navy"
                        : "bg-white text-simuflex-navy border-simuflex-line"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* KPI tiles — 6-up grid that collapses on narrow screens. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile
          label="Financed"
          value={data ? formatMoney(data.financedMinor, data.currency) : "—"}
          subtitle={data ? `${data.loansOpened} loans opened` : ""}
        />
        <KpiTile
          label="Collections"
          value={data ? formatMoney(data.collectionsMinor, data.currency) : "—"}
          subtitle="in window"
        />
        <KpiTile
          label="Net profit"
          value={data ? formatMoney(data.netProfitMinor, data.currency) : "—"}
          subtitle={
            data
              ? `${formatMoney(data.profitMinor, data.currency)} finance − opex`
              : "finance − opex"
          }
          accent
        />
        <KpiTile
          label="Outstanding"
          value={data ? formatMoney(data.outstandingMinor, data.currency) : "—"}
          subtitle="unpaid principal"
        />
        <KpiTile
          label="Overdue"
          value={data ? formatMoney(data.overdueMinor, data.currency) : "—"}
          subtitle={data ? `${data.arrearsLoanCount} loans` : ""}
          danger={data ? data.overdueMinor > 0 : false}
        />
        <KpiTile
          label="Active loans"
          value={data ? String(data.activeLoanCount) : "—"}
          subtitle={data ? `${data.defaultRatePct}% default rate` : ""}
        />
      </div>

      {/* Collections performance + accounting — second KPI row (UPGRADE-01 R6/R7). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile
          label="Expected collections"
          value={data ? formatMoney(data.expectedCollectionsMinor, data.currency) : "—"}
          subtitle="due in window"
        />
        <KpiTile
          label="Collection efficiency"
          value={data ? `${data.collectionEfficiencyPct}%` : "—"}
          subtitle="collected ÷ expected"
          danger={
            data ? data.expectedCollectionsMinor > 0 && data.collectionEfficiencyPct < 80 : false
          }
        />
        <KpiTile
          label="Operating expenses"
          value={data ? formatMoney(data.operatingExpensesMinor, data.currency) : "—"}
          subtitle="in window"
        />
        <KpiTile
          label="Deposits"
          value={data ? formatMoney(data.depositsMinor, data.currency) : "—"}
          subtitle="collected in window"
        />
        <KpiTile
          label="Due next 7 days"
          value={data ? formatMoney(data.upcomingWeekExpectedMinor, data.currency) : "—"}
          subtitle="forecast"
        />
        <KpiTile
          label="Due next 30 days"
          value={data ? formatMoney(data.upcomingMonthExpectedMinor, data.currency) : "—"}
          subtitle="forecast"
        />
      </div>

      {/* Receivables vs collected, per component (UPGRADE-02, ledger-sourced). */}
      <Card>
        <CardHeader>
          <CardTitle>Receivables &amp; income (all-time)</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-simuflex-slate">
                  <th className="py-1">Component</th>
                  <th className="py-1 text-right">Outstanding (receivable)</th>
                  <th className="py-1 text-right">Collected (earned)</th>
                </tr>
              </thead>
              <tbody className="tabular-nums text-simuflex-navy">
                <tr className="border-t border-simuflex-line">
                  <td className="py-1.5">Principal</td>
                  <td className="py-1.5 text-right">
                    {data
                      ? formatMoney(data.receivables.principalOutstandingMinor, data.currency)
                      : "—"}
                  </td>
                  <td className="py-1.5 text-right text-simuflex-slate">—</td>
                </tr>
                <tr className="border-t border-simuflex-line">
                  <td className="py-1.5">Interest</td>
                  <td className="py-1.5 text-right">
                    {data
                      ? formatMoney(data.receivables.interestOutstandingMinor, data.currency)
                      : "—"}
                  </td>
                  <td className="py-1.5 text-right">
                    {data
                      ? formatMoney(data.receivables.interestCollectedMinor, data.currency)
                      : "—"}
                  </td>
                </tr>
                <tr className="border-t border-simuflex-line">
                  <td className="py-1.5">Fees</td>
                  <td className="py-1.5 text-right">
                    {data?.receivables
                      ? formatMoney(data.receivables.feesOutstandingMinor, data.currency)
                      : "—"}
                  </td>
                  <td className="py-1.5 text-right">
                    {data?.receivables
                      ? formatMoney(data.receivables.feesCollectedMinor, data.currency)
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-simuflex-slate">
            Sourced from the ledger. Interest/fees populate once payments post the split shape; a
            zero row means that account hasn&apos;t been re-posted yet.
          </p>
        </CardBody>
      </Card>

      {/* Trend + status donut. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue trend</CardTitle>
          </CardHeader>
          <CardBody>
            {data ? (
              <LineChart
                xLabels={data.trend.map((b) =>
                  formatBucketLabel(b.bucketStart, data.window.bucket),
                )}
                series={[
                  {
                    label: "Financed",
                    colour: "#1A3B69",
                    // The chart plots MAJOR units so the Y-axis labels
                    // read like real money. `toMajor` handles per-
                    // currency precision (TZS / KES / UGX are 0-decimal,
                    // others 2). Previously the chart divided by 100
                    // unconditionally and the formatter multiplied back
                    // — a double conversion that under-displayed TZS by
                    // 100×.
                    values: data.trend.map((b) => toMajor(b.financedMinor, data.currency)),
                  },
                  {
                    label: "Collections",
                    colour: "#1DB954",
                    values: data.trend.map((b) => toMajor(b.collectionsMinor, data.currency)),
                  },
                  {
                    label: "Expected",
                    colour: "#9CA3AF",
                    values: data.trend.map((b) => toMajor(b.expectedMinor, data.currency)),
                  },
                  {
                    label: "Profit",
                    colour: "#FF6B35",
                    values: data.trend.map((b) => toMajor(b.profitMinor, data.currency)),
                  },
                ]}
                formatY={(v) => compactMajor(v, data.currency)}
                heightPx={240}
              />
            ) : (
              <p className="text-xs text-simuflex-slate">Loading…</p>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Loans by status</CardTitle>
          </CardHeader>
          <CardBody>
            {data ? (
              <StatusDonut
                segments={data.byStatus.map((row) => ({
                  key: row.status,
                  label: shortStatus(row.status),
                  value: row.count,
                  colour: statusColour(row.status),
                }))}
              />
            ) : (
              <p className="text-xs text-simuflex-slate">Loading…</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Top devices */}
      <Card>
        <CardHeader>
          <CardTitle>Top devices</CardTitle>
        </CardHeader>
        <CardBody>
          {data ? (
            <HorizontalBars
              rows={data.topDevices.map((d) => ({
                label: `${d.brand} ${d.model}`,
                value: d.count,
                subtitle: `${formatMoney(d.financedMinor, data.currency)} financed`,
              }))}
            />
          ) : (
            <p className="text-xs text-simuflex-slate">Loading…</p>
          )}
        </CardBody>
      </Card>

      {report.isError ? (
        <p className="text-xs text-red-600">
          Failed to load report — {(report.error as Error)?.message ?? "unknown error"}.
        </p>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  Subcomponents + helpers
 * ──────────────────────────────────────────────────────────────────── */

function KpiTile({
  label,
  value,
  subtitle,
  accent = false,
  danger = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly accent?: boolean;
  readonly danger?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-xl border bg-white p-3 ${
        danger
          ? "border-red-200"
          : accent
            ? "border-simuflex-green/50 bg-simuflex-green/5"
            : "border-simuflex-line"
      }`}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-simuflex-slate">{label}</p>
      <p
        className={`mt-1 text-xl font-extrabold ${danger ? "text-red-700" : "text-simuflex-navy"}`}
      >
        {value}
      </p>
      {subtitle ? <p className="text-[11px] text-simuflex-slate mt-0.5">{subtitle}</p> : null}
    </div>
  );
}

/**
 * Number of fractional digits a currency's MAJOR unit has.
 * TZS / KES / UGX are zero-decimal currencies — the DB's `*_minor`
 * column literally stores the whole-number amount; dividing by 100
 * would under-display by 100×. Other ISO-4217 codes default to 2.
 *
 * Mirrors the shared `formatMoney` in apps/admin-web/src/lib/utils.ts.
 * Kept inline here (rather than re-importing) because the local
 * helper is also used by `formatCompactMoney` below + by DashboardPage,
 * and a single source of truth avoids version drift between the
 * compact and verbose formats.
 */
function decimalsFor(currency: string): number {
  switch (currency) {
    case "TZS":
    case "KES":
    case "UGX":
    case "RWF":
    case "BIF":
      return 0;
    default:
      return 2;
  }
}

export function formatMoney(amountMinor: number, currency: string): string {
  const divisor = Math.pow(10, decimalsFor(currency));
  const major = amountMinor / divisor;
  const formatted = Math.round(major).toLocaleString();
  return `${currency} ${formatted}`;
}

export function formatCompactMoney(amountMinor: number, currency: string): string {
  const divisor = Math.pow(10, decimalsFor(currency));
  const major = amountMinor / divisor;
  if (major === 0) return `${currency} 0`;
  if (major >= 1_000_000) return `${currency} ${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${currency} ${Math.round(major / 1_000)}k`;
  return `${currency} ${Math.round(major).toLocaleString()}`;
}

/** Convert minor units to a chart-friendly major-units number. */
export function toMajor(amountMinor: number, currency: string): number {
  return amountMinor / Math.pow(10, decimalsFor(currency));
}

/**
 * Compact format an ALREADY-major-units number — used by LineChart
 * Y-axis ticks where the data has already been converted via
 * `toMajor`. Saves a redundant minor-major-minor-major round-trip
 * that previously cancelled out the per-currency precision.
 */
function compactMajor(major: number, currency: string): string {
  if (major === 0) return `${currency} 0`;
  if (major >= 1_000_000) return `${currency} ${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${currency} ${Math.round(major / 1_000)}k`;
  return `${currency} ${Math.round(major).toLocaleString()}`;
}

function formatBucketLabel(iso: string, bucket: string): string {
  const d = new Date(iso);
  if (bucket === "month") {
    return d.toLocaleString(undefined, { month: "short", year: "2-digit" });
  }
  if (bucket === "week") {
    const day = d.getDate();
    const month = d.toLocaleString(undefined, { month: "short" });
    return `${day} ${month}`;
  }
  const day = d.getDate();
  const month = d.toLocaleString(undefined, { month: "short" });
  return `${day} ${month}`;
}

function shortStatus(status: string): string {
  switch (status) {
    case "PENDING_VERIFICATION":
      return "Verifying";
    case "PENDING_DEPOSIT":
      return "Awaiting dep.";
    case "PENDING_DEVICE_PROVISIONING":
      return "Provision dev.";
    case "ACTIVE":
      return "Active";
    case "IN_ARREARS":
      return "Arrears";
    case "LOCKED":
      return "Locked";
    case "IN_DISPUTE":
      return "Dispute";
    case "PENDING_RELEASE":
      return "Releasing";
    case "PAID_OFF":
      return "Paid off";
    case "WRITTEN_OFF":
      return "Written off";
    case "REJECTED":
      return "Rejected";
    case "DRAFT":
      return "Draft";
    default:
      return status;
  }
}

function statusColour(status: string): string {
  switch (status) {
    case "PENDING_VERIFICATION":
      return "#D97706";
    case "PENDING_DEPOSIT":
      return "#1A3B69";
    case "PENDING_DEVICE_PROVISIONING":
      return "#9A3412";
    case "ACTIVE":
    case "PAID_OFF":
      return "#137A3B";
    case "IN_ARREARS":
    case "LOCKED":
    case "WRITTEN_OFF":
      return "#C53030";
    case "IN_DISPUTE":
      return "#7C3AED";
    case "PENDING_RELEASE":
      return "#0EA5E9";
    default:
      return "#6C757D";
  }
}
